import React, { useEffect } from "react";
import type { RouteComponentProps } from "@gatsbyjs/reach-router";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { navigate } from "utilities/navigate";
import {
  Form,
  Typography,
  Input,
  DateInput,
  Checkbox,
  Button,
  PageLoader,
  Alert,
  toast,
} from "@picklebet/ui";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { userVerificationSchema } from "./schemas/userVerificationSchema";
import { ALPHANUMERIC, LEGAL_NAME } from "utilities/regex";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { verificationService } from "features/account/services/verification";
import { useVerificationFormAttempt } from "features/account/hooks/useVerifications";
import { useCustomerLicence } from "@/features/auth/hooks/usePermission";
import { handleFailedVerificationAttempt } from "features/account/utils/verification";
import { telemetry } from "@/utilities/telemetry";
import { DVSConsent } from "@/features/account/components/verification/DVSConsent";
import { VERIFICATION_DATA_SOURCES } from "features/account/types/Verification";
import { isValidFutureDate } from "utilities/datetime";

const schema = userVerificationSchema.extend({
  middleName: z.string().regex(LEGAL_NAME, "Invalid middle name").optional(),
  number: z
    .string()
    .min(1, "Passport document number is required")
    .min(8, "Valid Passport number is required")
    .regex(ALPHANUMERIC, "Only alphanumeric characters are allowed")
    .max(9, "Valid Passport number is required"),
  expiryDate: z
    .string({ error: "A valid expiry date is required" })
    .refine(isValidFutureDate, {
      message: "Your expiry date is invalid or passport has expired",
    }),
});

type FormValues = z.infer<typeof schema>;

type AustralianPassportProps = {
  prefix?: string;
} & RouteComponentProps;

const AustralianPassport = ({
  prefix = "/account",
}: AustralianPassportProps) => {
  const licence = useCustomerLicence();
  const { attempt, isLoading, hasSubmitted, markSubmitted } =
    useVerificationFormAttempt();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const { profile } = useProfile();

  useEffect(() => {
    if (profile) {
      setValue("firstName", profile.firstName);
      setValue("lastName", profile.lastName);
      setValue("dateOfBirth", profile.dateOfBirth);
    }
  }, [profile]);

  const consent = watch("consent", false);

  const onSubmit = async ({ consent, ...fields }: FormValues) => {
    if (!attempt) {
      toast({
        variant: "danger",
        description:
          "Something went wrong. Please refresh and try again or contact support.",
      });
      return;
    }

    const verificationType = VERIFICATION_DATA_SOURCES.AUSTRALIAN_PASSPORT;

    const result = await verificationService.submitAttemptData({
      attemptId: attempt.attemptId,
      attemptType: "VIXIdentityDocuments",
      type: "AustralianPassport",
      terms: consent,
      fields,
    });

    if (!result.ok) {
      handleFailedVerificationAttempt(verificationType, licence);
      return;
    }

    telemetry.submittedVerificationForm({
      outcome: "success",
      type: verificationType,
      licence: licence ?? undefined,
    });

    markSubmitted();
    navigate(`${prefix}/verification`);
  };

  if (isLoading || hasSubmitted) return <PageLoader />;

  if (!attempt && !isSubmitting) {
    return (
      <Alert
        color="danger"
        actions={
          <Button
            onClick={() => window.location.reload()}
            size="md"
            variant="danger"
          >
            Refresh
          </Button>
        }
      >
        Unable to load verification option, please refresh to try again.
      </Alert>
    );
  }

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        fixedWidth
        disabled={isSubmitting}
      >
        <Typography variant="h4" noSpacing>
          Australian Passport
        </Typography>

        <Typography variant="body">
          Please enter your identity information, exactly as it appears on your
          Australian Passport.
        </Typography>

        <Typography variant={`body`}>
          Don't have a Passport? Select from a list of{" "}
          <Link to={`${prefix}/verification`}>Verification options</Link>.
        </Typography>

        <Input
          label={`Passport Number`}
          {...register("number")}
          errorMessage={errors?.number?.message}
          maxLength={9}
        />
        <Controller
          control={control}
          name={"expiryDate"}
          render={({ field: { ref: _, ...field } }) => (
            <DateInput
              id={"expiryDate"}
              {...field}
              errorMessage={errors.expiryDate?.message}
              label={`Expiry Date`}
            />
          )}
        />

        <Input
          label={`First Name`}
          {...register("firstName")}
          errorMessage={errors?.firstName?.message}
        />

        <Input
          label={`Middle Name`}
          {...register("middleName")}
          errorMessage={errors?.middleName?.message}
        />

        <Input
          label={`Last Name`}
          {...register("lastName")}
          errorMessage={errors?.lastName?.message}
        />

        <Controller
          control={control}
          name={"dateOfBirth"}
          render={({ field: { ref: _, ...field } }) => (
            <DateInput
              id={"dateOfBirth"}
              label={`Date of Birth`}
              hideLabel
              {...field}
              errorMessage={errors.dateOfBirth?.message}
            />
          )}
        />

        <Controller
          control={control}
          render={({ field: { onChange, value: _, ...field } }) => (
            <Checkbox
              id={`consent`}
              label={DVSConsent}
              checked={consent}
              onCheckedChange={(checked) => onChange?.(checked === true)}
              hasError={!!errors?.consent?.message}
              {...field}
            />
          )}
          name={`consent`}
        />

        <Button type={`submit`} loading={isSubmitting}>
          Verify Passport
        </Button>
      </Form>
    </>
  );
};

export { AustralianPassport };
